'use client';

import type { CSSProperties, ReactNode } from 'react';

import { useImageDominantColor } from '@ps/functions/hooks/useImageDominantColor';
import { getContrastingTextColor } from '@ps/functions/utils/color';

import { useBannerColorSourceUrl } from '../../../../series/shared/hooks/useBannerColorSourceUrl';
import { adjustColorForBackground } from '../../../custom-button/functions/colors';

interface Props {
  /** Banners whose dominant color tints the card — sampled per-viewport (the
   *  mobile banner drives the tint < sm when present, matching the CSS image
   *  swap). Both absent → the card keeps the neutral defaults (light surface,
   *  dark text). */
  desktopBannerUrl?: string | null;
  mobileBannerUrl?: string | null;
  /** Root card classes (layout + border) — the scope owns only the colors. */
  className?: string;
  children: ReactNode;
}

// Neutral surface used for the server render + no-JS / CORS-failure fallback,
// 1:1 with legacy `SeriesView`'s light-theme defaults. The client hook swaps in
// the banner-derived color once the image decodes.
const DEFAULT_BG = '#f0f0f0';
const DEFAULT_TEXT = '#000000';

/**
 * Scoped client color plugin for a series/course card. The card body is
 * server-rendered HTML; this thin wrapper is the one interactive leaf — it
 * samples the banner's average pixel (via {@link useImageAverageColor}) and
 * exposes the result as the card's `backgroundColor` + `color` (so
 * `currentColor` name/buttons pick up the contrasting shade) + a
 * `--series-card-footer-bg` var. 1:1 with legacy `Series.tsx`'s banner-color
 * extraction: the DOMINANT color (`useImageDominantColor` — most-common bucket,
 * NOT the muddy average), run through `adjustColorForBackground` (darkens a too-
 * light color 30%), with the contrasting text recomputed on the ADJUSTED color —
 * exactly legacy's `extractDominantColor → adjustColorForBackground →
 * setSeriesBackgroundColor / setSeriesTextColor(getContrastingTextColor(...))`.
 * Falls back to the neutral defaults when there's no banner or the image can't
 * be sampled (CORS).
 */
export function SeriesCardColorScope({
  desktopBannerUrl,
  mobileBannerUrl,
  className,
  children,
}: Props) {
  // The probe follows the same `sm:` breakpoint the banner images swap on, so
  // a separate mobile banner tints the card with ITS palette on phones (QA).
  const colorSourceUrl = useBannerColorSourceUrl(
    desktopBannerUrl ?? null,
    mobileBannerUrl ?? null,
  );
  const { color } = useImageDominantColor(colorSourceUrl);
  // Legacy adjusts the dominant color for use as a background (darken if too
  // light), then derives the text color from the ADJUSTED color.
  const bgColor = color ? adjustColorForBackground(color) : DEFAULT_BG;
  const textColor = color ? getContrastingTextColor(bgColor) : DEFAULT_TEXT;

  const style = {
    backgroundColor: bgColor,
    color: textColor,
    borderColor: '#ffffff2b',
    '--series-card-footer-bg':
      textColor === '#ffffff' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
  } as CSSProperties;

  return (
    <div className={className} style={style}>
      {children}
    </div>
  );
}
